import React, { useState, type ReactNode } from "react";
import { Pressable, Text, StyleSheet, View } from "react-native";
import {
  colors,
  spacing,
  radii,
  shadowStyles,
  typographyStyles,
  typographyScale,
  webTransitions,
} from "../../../styles/design-system";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import AxioCard from "../../axio-ds/Card/AxioCard";
import { IconCircle } from "../ContentSafety/IconCircle";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../common/general/CustomText/CustomText";
import { ArrowRight } from "lucide-react";

export type StatCardColorType = "blue" | "purple" | "green" | "orange" | "red";
export type CourseInfoStatCardColorType = "info" | "purple" | "success";

const colorConfig: Record<
  StatCardColorType,
  {
    border: string;
    hoverBorder: string;
    hoverShadow: string;
    iconBg: string;
    iconHoverBg: string;
    iconColor: string;
    accentText: string;
  }
> = {
  blue: {
    border: colors.blue[100],
    hoverBorder: colors.blue[200],
    hoverShadow: colors.blue[100],
    iconBg: colors.blue[50],
    iconHoverBg: colors.blue[100],
    iconColor: colors.blue[500],
    accentText: colors.blue[700],
  },
  purple: {
    border: colors.purple[100],
    hoverBorder: colors.purple[200],
    hoverShadow: colors.purple[100],
    iconBg: colors.purple[50],
    iconHoverBg: colors.purple[100],
    iconColor: colors.purple[500],
    accentText: colors.purple[700],
  },
  green: {
    border: colors.success[100],
    hoverBorder: colors.success[200],
    hoverShadow: colors.success[100],
    iconBg: colors.success[50],
    iconHoverBg: colors.success[100],
    iconColor: colors.success[500],
    accentText: colors.success[700],
  },
  orange: {
    border: colors.warning[100],
    hoverBorder: colors.warning[200],
    hoverShadow: colors.warning[100],
    iconBg: colors.warning[50],
    iconHoverBg: colors.warning[100],
    iconColor: colors.warning[400],
    accentText: colors.warning[700],
  },
  red: {
    border: colors.error[100],
    hoverBorder: colors.error[200],
    hoverShadow: colors.error[100],
    iconBg: colors.error[50],
    iconHoverBg: colors.error[100],
    iconColor: colors.error[500],
    accentText: colors.error[700],
  },
};

const courseInfoColorConfig: Record<
  CourseInfoStatCardColorType,
  {
    borderColor: string;
    hoverBorderColor: string;
  }
> = {
  info: {
    borderColor: "rgb(56, 189, 248)",
    hoverBorderColor: "rgb(56, 189, 248)",
  },
  purple: {
    borderColor: "rgb(212, 194, 247)",
    hoverBorderColor: "rgb(212, 194, 247)",
  },
  success: {
    borderColor: "rgb(166, 239, 103)",
    hoverBorderColor: "rgb(166, 239, 103)",
  },
};

interface DefaultStatCardProps {
  title: string;
  value: string;
  change: string;
  changeType: "positive" | "negative";
  icon: PhosphorIcon;
  color: StatCardColorType;
  variant?: "default";
  footer?: React.ReactNode;
  type?: "base" | "actionable";
  subtitles?: string;
  trend?: string;
  handlePress?: () => void;
}

interface CourseInfoStatCardProps {
  variant: "courseInfo";
  icon: ReactNode;
  value: string;
  label: string;
  color: CourseInfoStatCardColorType;
  iconBackgroundColor?: string;
}

interface SummaryStatCardProps {
  variant: "summary";
  icon: ReactNode;
  value: string;
  label: string;
  color: StatCardColorType;
  iconBackgroundColor?: string;
}

type StatCardProps = DefaultStatCardProps | CourseInfoStatCardProps | SummaryStatCardProps;

export function StatCard(props: StatCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  if (props.variant === "summary") {
    const { icon, value, label, color, iconBackgroundColor } = props;
    const colorStyles = colorConfig[color];

    return (
      <View
        style={[
          styles.summaryPressableWrapper,
          styles.summaryCard,
          shadowStyles.xs,
          { borderColor: colors.grey[200] },
        ]}>
        <View style={styles.summaryCardContent}>
          <View style={[styles.summaryIconContainer, { backgroundColor: iconBackgroundColor ?? colorStyles.iconBg }]}>
            {icon}
          </View>
          <View style={styles.summaryContent}>
            <CustomText
              text={value}
              size="m"
              textType="display"
              weight="bold"
              useTranslationText={false}
              style={styles.summaryValueText}
            />
            <CustomText
              text={label}
              size="xs"
              weight="medium"
              useTranslationText={false}
              style={styles.summaryLabelText}
            />
          </View>
        </View>
      </View>
    );
  }

  if (props.variant === "courseInfo") {
    const { icon, value, label, color, iconBackgroundColor } = props;
    const colorStyles = courseInfoColorConfig[color];

    return (
      <Pressable
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessible={false}
        style={[
          styles.courseInfoPressableWrapper,
          styles.courseInfoCard,
          shadowStyles.sm,
          isHovered && shadowStyles.lg,
          { borderColor: isHovered ? colorStyles.hoverBorderColor : colorStyles.borderColor },
        ]}>
        <View style={styles.courseInfoCardContent}>
          <IconCircle
            variant="default"
            size="lg"
            rounded="full"
            icon={icon}
            style={iconBackgroundColor ? { backgroundColor: iconBackgroundColor } : undefined}
          />
          <View style={styles.courseInfoContent}>
            <CustomText
              text={value}
              size="m"
              textType="display"
              weight="bold"
              useTranslationText={false}
              style={styles.courseInfoValueText}
            />
            <CustomText
              text={label}
              size="xs"
              weight="medium"
              useTranslationText={false}
              style={styles.courseInfoLabelText}
            />
          </View>
        </View>
      </Pressable>
    );
  }
  if (!props.variant || props.variant === "default") {
    const { title, value, change, changeType, icon: Icon, color, footer } = props;
    const colorStyles = colorConfig[color];

    const shadowStyle = isHovered
      ? {
          shadowColor: props.type === "actionable" ? "" : colorStyles.hoverShadow,
          shadowOffset: props.type === "actionable" ? { width: 0, height: 10 } : { width: 0, height: 20 },
          shadowOpacity: props.type === "actionable" ? 0.1 : 0.5,
          shadowRadius: props.type === "actionable" ? 20 : 25,
          elevation: 8,
        }
      : {
          shadowColor: colors.grey[900],
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.1,
          shadowRadius: 6,
          elevation: 4,
        };

    return (
      <Pressable
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onPress={props.type === "actionable" ? props.handlePress : undefined}
        style={styles.defaultPressableWrapper}>
        <AxioCard
          variant="basic"
          style={[
            styles.container,
            shadowStyle,
            { borderColor: isHovered ? colorStyles.hoverBorder : colorStyles.border },
          ]}>
          <View style={styles.content}>
            <View style={styles.header}>
              <View
                style={[
                  styles.iconContainer,
                  {
                    backgroundColor:
                      isHovered && props.type !== "actionable" ? colorStyles.iconHoverBg : colorStyles.iconBg,
                  },
                ]}>
                <Icon size={24} color={colorStyles.iconColor} weight="regular" />
              </View>
              {change && (
                <View
                  style={[
                    styles.changeBadge,
                    changeType === "positive" ? styles.changeBadgePositive : styles.changeBadgeNegative,
                  ]}>
                  <Text
                    style={[
                      styles.changeText,
                      changeType === "positive" ? styles.changeTextPositive : styles.changeTextNegative,
                    ]}>
                    {change}
                  </Text>
                </View>
              )}
            </View>
            <Text style={styles.valueText}>{value}</Text>
            <Text style={styles.titleText}>{title}</Text>
            {footer && <View style={styles.footerContainer}>{footer}</View>}
            {props.type === "actionable" && (
              <View>
                <CustomSpacing type="vertical" size="m" />
                <Text>{props.subtitles}</Text>
                <CustomSpacing type="vertical" size="s" />
              </View>
            )}
            {props.type === "actionable" && (
              <View style={{ flexDirection: "row", gap: 4, alignItems: "center" }}>
                <CustomText
                  text={props.trend}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: colorStyles.iconColor }}
                />
                <ArrowRight
                  style={{ color: colorStyles.iconColor, width: 12, height: 12, display: isHovered ? "block" : "none" }}
                />
              </View>
            )}
          </View>
        </AxioCard>
      </Pressable>
    );
  }
}

const styles = StyleSheet.create({
  defaultPressableWrapper: {
    flex: 1,
    // width: "32%",
    // height: 220,
    borderRadius: 24,
    // @ts-expect-error web-only property
    cursor: "default",
  },
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: 24,
    padding: spacing.xl,
    borderWidth: 1,
    ...webTransitions.fast,
  },
  content: {
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: spacing.lg,
  },
  footerContainer: {
    marginTop: 6,
  },
  iconContainer: {
    width: spacing["3xl"],
    height: spacing["3xl"],
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  changeBadge: {
    paddingHorizontal: 10,
    paddingVertical: spacing.xxs,
    borderRadius: radii.full,
  },
  changeBadgePositive: {
    backgroundColor: colors.success[50],
  },
  changeBadgeNegative: {
    backgroundColor: colors.error[50],
  },
  changeText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
  },
  changeTextPositive: {
    color: colors.success[700],
  },
  changeTextNegative: {
    color: colors.error[700],
  },
  valueText: {
    fontSize: typographyScale.displayXl.fontSize,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[900],
    marginBottom: spacing.xs,
  },
  titleText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: typographyStyles.fontRegular.fontWeight,
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  summaryPressableWrapper: {
    flex: 1,
    minWidth: 220,
    borderRadius: radii.lg,
    // @ts-expect-error web-only property
    cursor: "default",
  },
  summaryCard: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...webTransitions.fast,
  },
  summaryCardContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  summaryIconContainer: {
    width: 40,
    height: 40,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  summaryContent: {
    flex: 1,
    gap: 2,
  },
  summaryValueText: {
    color: colors.grey[900],
    fontSize: typographyScale.displaySm.fontSize,
    lineHeight: typographyScale.displaySm.lineHeight,
    fontWeight: typographyStyles.fontSemibold.fontWeight,
  },
  summaryLabelText: {
    color: colors.grey[600],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  courseInfoPressableWrapper: {
    flex: 1,
    minWidth: 240,
    borderRadius: radii.xl,
    // @ts-expect-error web-only property
    cursor: "default",
  },
  courseInfoCard: {
    backgroundColor: "rgba(255, 255, 255, 0.4)",
    borderRadius: radii.xl,
    borderWidth: 1,
    padding: spacing.lg,
    ...webTransitions.fast,
    // @ts-expect-error web-only property
    backdropFilter: "blur(8px)",
  },
  courseInfoCardContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  courseInfoContent: {
    flex: 1,
  },
  courseInfoValueText: {
    color: colors.grey[900],
    fontSize: 30,
    lineHeight: 38,
    fontWeight: "400",
    letterSpacing: -0.3,
    fontVariant: ["tabular-nums"],
  },
  courseInfoLabelText: {
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 4,
  },
});
